import { type Dispatch, type SetStateAction } from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { DEFAULT_LANGUAGE } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { useFlag } from '../../services/launchDarkly';
import type { Category } from '../../stores/categories/CategoriesStore';
import { useStores } from '../../stores/useStores';
import Chevron from '../SVGIcons/Chevron/Chevron';

import { GameCard } from './GameCard';
import styles from './GamesCarousel.module.scss';
import { seenGamesLobbyListener } from './seenGamesImpression';

const scroll = (key: any, scrollOffset: number) => {
  const selector = `[data-scroll="${key}"]`;
  const el = document.querySelector(selector);
  if (!el) return;
  el.scrollTo({
    left: el.scrollLeft + scrollOffset,
    behavior: 'smooth',
  });
};

const listener = throttle(function listener(ev) {
  seenGamesLobbyListener();

  const { scrollLeft, scrollWidth, clientWidth } = ev.target;
  const el = ev.target.parentNode;

  if (scrollLeft + clientWidth >= scrollWidth - 10) {
    el.classList.add(styles.isEnd);
  } else {
    el.classList.remove(styles.isEnd);
  }

  if (scrollLeft < 10) {
    el.classList.add(styles.isStart);
  } else {
    el.classList.remove(styles.isStart);
  }
}, 100);

export const GamesCarousel = observer(function GamesCarousel(props: {
  id: Category['masterGameSubCategoryId'];
  onboardingRow: boolean;
  setReferenceElement: Dispatch<SetStateAction<HTMLElement | null>>;
}) {
  const { categoriesStore } = useStores();
  const { i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);

  const gameLabelFF = useFlag('game_labels_feat', true);

  const category = categoriesStore.categoriesRegistry[props.id];
  const games = category.subCategoryGames;

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapperOnboarding]: props.onboardingRow,
        [styles.isStart]: true,
      })}
    >
      {games.length > 6 && (
        <button className={classNames(styles.arrow, styles.arrowLeft)} onClick={() => scroll(props.id, -300)}>
          <Chevron direction="left" />
        </button>
      )}
      <div
        className={classNames(styles.carousel, {
          [styles.carouselOnboarding]: props.onboardingRow,
        })}
        onScroll={listener}
      >
        <div className={styles.gamesBlock}>
          {games.map((game, gameIdx) => (
            <GameCard
              key={`${game.identifier}::${game.masterCasinoGameId}`}
              carousel
              game={game}
              gameLabelFF={gameLabelFF}
              onboarding={props.onboardingRow && gameIdx === 1}
              rowName={category.name[language] || category.name[DEFAULT_LANGUAGE]}
              tooltipRef={props.onboardingRow && gameIdx === 1 ? props.setReferenceElement : null}
            />
          ))}
        </div>
      </div>
      {games.length > 6 && (
        <button className={classNames(styles.arrow, styles.arrowRight)} onClick={() => scroll(props.id, 300)}>
          <Chevron direction="right" />
        </button>
      )}
    </div>
  );
});
